import {use, useCallback} from 'react'
import type {RefObject} from 'react'
import {Banner, Stack} from '@primer/react'
import type {StackProps} from '@primer/react'
import {InternalBannerStackDataContext} from './internal-banner-stack-data-context'
import {InternalBannerRegistryContext} from './internal-banner-registry-context'
import {useDismissBanner, useFocusBanner} from './hooks'

export interface BannerStackOutletProps extends StackProps<'div'> {
  /** Element that receives focus when a user dismisses the only banner in the stack. */
  focusFallbackRef: RefObject<HTMLElement | null>
}

export function BannerStackOutlet({focusFallbackRef, ...props}: BannerStackOutletProps) {
  const {banners} = use(InternalBannerStackDataContext)
  const {registerBannerEl} = use(InternalBannerRegistryContext)
  const dismissBanner = useDismissBanner()
  const focusBanner = useFocusBanner()

  const handleDismiss = useCallback(
    (id: string) => {
      const index = banners.findIndex(banner => banner.id === id)
      const focusTarget = banners[index - 1] ?? banners[index + 1]

      dismissBanner(id)

      if (focusTarget) {
        focusBanner(focusTarget.id)
      } else {
        focusFallbackRef?.current?.focus()
      }
    },
    [banners, dismissBanner, focusBanner, focusFallbackRef],
  )

  return (
    <Stack gap="condensed" direction="vertical" {...props}>
      {banners.map(({dismissible, onDismiss, ...banner}) => (
        <Banner
          key={banner.id}
          ref={el => registerBannerEl(banner.id, el)}
          {...banner}
          onDismiss={
            dismissible
              ? () => {
                  handleDismiss(banner.id)
                  onDismiss?.()
                }
              : undefined
          }
        />
      ))}
    </Stack>
  )
}

BannerStackOutlet.displayName = 'BannerStackOutlet'
